import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import React, { useEffect, useMemo, useState } from 'react';
import { Bar, Line, Pie } from 'react-chartjs-2';
import { useNavigate } from 'react-router-dom';
import { useScreenLogging } from '../../../hooks/useScreenLogging';
import { typography } from '../../../styles/typography';
import { getAllSites } from '../../SiteManagement/siteApi';
import Card from '../../ui/card';
import { stockDashboardApi } from './StockDashboardApi';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

// Format date for API (YYYY-MM-DD)
const formatDateForAPI = (date) => {
  return date.toISOString().split('T')[0];
};

// Format date for display
const formatDate = (date) => {
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

// Get current date
const getCurrentDate = () => {
  return new Date();
};

// Note: Dummy data generation function removed - now using real API via stockDashboardApi

const StockDashboard = () => {
  // Log screen view
  useScreenLogging('StockDashboard', {
    component: 'StockDashboard',
  });
  const navigate = useNavigate();
  const [sites, setSites] = useState([]);
  const [isSitesLoading, setIsSitesLoading] = useState(true);
  const [selectedSite, setSelectedSite] = useState('');
  const [period, setPeriod] = useState('weekly');
  const [selectedDate, setSelectedDate] = useState(formatDateForAPI(getCurrentDate()));
  const [startDate, setStartDate] = useState(null); // Date object or null
  const [endDate, setEndDate] = useState(null); // Date object or null
  const [isLoading, setIsLoading] = useState(false);
  const [stockData, setStockData] = useState(null);
  const [error, setError] = useState(null);

  // Fetch sites on component mount (with fallback to dummy sites)
  useEffect(() => {
    const fetchSites = async () => {
      setIsSitesLoading(true);
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
          ? response
          : [];
        const sorted = [...list].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        setSites(sorted.length > 0 ? sorted : [
          { site_id: 'site-001', site_name: 'Koramangala Outlet' },
          { site_id: 'site-002', site_name: 'Indiranagar Branch' },
          { site_id: 'site-003', site_name: 'Whitefield Center' },
          { site_id: 'site-004', site_name: 'MG Road Store' },
          { site_id: 'site-005', site_name: 'Electronic City' },
        ]);
        // Don't auto-select - let user select from dropdown
      } catch (error) {
        console.error('Error fetching sites, using fallback:', error);
        // Use dummy sites as fallback
        setSites([
          { site_id: 'site-001', site_name: 'Koramangala Outlet' },
          { site_id: 'site-002', site_name: 'Indiranagar Branch' },
          { site_id: 'site-003', site_name: 'Whitefield Center' },
          { site_id: 'site-004', site_name: 'MG Road Store' },
          { site_id: 'site-005', site_name: 'Electronic City' },
        ]);
      } finally {
        setIsSitesLoading(false);
      }
    };

    fetchSites();
  }, []);

  // Fetch stock dashboard data when filters change
  useEffect(() => {
    const loadStockData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        let apiParams = {};
        
        // Handle custom period
        if (period === 'custom') {
          if (!startDate || !endDate) {
            setIsLoading(false);
            return;
          }
          // Ensure dates are formatted correctly (handle both Date objects and strings)
          const startDateStr = startDate instanceof Date 
            ? formatDateForAPI(startDate) 
            : startDate;
          const endDateStr = endDate instanceof Date 
            ? formatDateForAPI(endDate) 
            : endDate;
          
          apiParams = {
            period: 'custom',
            start_date: startDateStr,
            end_date: endDateStr,
          };
        } else {
          // For daily/weekly/monthly periods
          // selectedDate is already a string in YYYY-MM-DD format
          apiParams = {
            period: period,
            date: selectedDate || formatDateForAPI(getCurrentDate()),
          };
        }

        // Add site filter if selected
        if (selectedSite) {
          apiParams.site_id = selectedSite;
        }

        // Add limit for top/least lists
        apiParams.limit = 10;

        console.log('Fetching stock dashboard data with params:', apiParams);

        // Call the API
        const result = await stockDashboardApi.getStockDashboard(apiParams);

        if (result.success && result.data) {
          setStockData(result.data);
          console.log('Stock dashboard data loaded successfully');
        } else {
          const errorMessage = result.message || 'Failed to load stock dashboard data';
          setError(errorMessage);
          console.error('API Error:', result.error, errorMessage);
          setStockData(null);
        }
      } catch (err) {
        console.error('Error loading stock dashboard data:', err);
        setError(err.message || 'Failed to load stock dashboard data');
        setStockData(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadStockData();
  }, [period, selectedDate, startDate, endDate, selectedSite]);

  // Chart data preparation
  const daywiseStockData = useMemo(() => {
    if (!stockData?.daywise_stock_analytics) return null;

    const sorted = [...stockData.daywise_stock_analytics].sort((a, b) =>
      a.date.localeCompare(b.date)
    );

    return {
      labels: sorted.map((item) => {
        const d = new Date(item.date);
        return formatDate(d);
      }),
      datasets: [
        {
          label: 'Opening Stock',
          data: sorted.map((item) => item.opening_stock_quantity || 0),
          borderColor: '#3b82f6',
          backgroundColor: 'rgba(59, 130, 246, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Closing Stock',
          data: sorted.map((item) => item.closing_stock_quantity || 0),
          borderColor: '#22c55e',
          backgroundColor: 'rgba(34, 197, 94, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Stock In',
          data: sorted.map((item) => item.stock_in_quantity || 0),
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Stock Out',
          data: sorted.map((item) => item.stock_out_quantity || 0),
          borderColor: '#ef4444',
          backgroundColor: 'rgba(239, 68, 68, 0.1)',
          fill: true,
          tension: 0.4,
        },
      ],
    };
  }, [stockData]);

  const stockInOverTimeData = useMemo(() => {
    if (!stockData?.stock_in_analytics?.over_time) return null;

    const sorted = [...stockData.stock_in_analytics.over_time].sort((a, b) =>
      a.date.localeCompare(b.date)
    );

    return {
      labels: sorted.map((item) => {
        const d = new Date(item.date);
        return formatDate(d);
      }),
      datasets: [
        {
          label: 'Stock In Quantity',
          data: sorted.map((item) => item.quantity || 0),
          borderColor: '#10b981',
          backgroundColor: 'rgba(16, 185, 129, 0.2)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Stock In Value (₹)',
          data: sorted.map((item) => item.value || 0),
          borderColor: '#059669',
          backgroundColor: 'rgba(5, 150, 105, 0.2)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y1',
        },
      ],
    };
  }, [stockData]);

  const stockOutOverTimeData = useMemo(() => {
    if (!stockData?.stock_out_analytics?.over_time) return null;

    const sorted = [...stockData.stock_out_analytics.over_time].sort((a, b) =>
      a.date.localeCompare(b.date)
    );

    return {
      labels: sorted.map((item) => {
        const d = new Date(item.date);
        return formatDate(d);
      }),
      datasets: [
        {
          label: 'Stock Out Quantity',
          data: sorted.map((item) => item.quantity || 0),
          borderColor: '#ef4444',
          backgroundColor: 'rgba(239, 68, 68, 0.2)',
          fill: true,
          tension: 0.4,
        },
        {
          label: 'Stock Out Value (₹)',
          data: sorted.map((item) => item.value || 0),
          borderColor: '#dc2626',
          backgroundColor: 'rgba(220, 38, 38, 0.2)',
          fill: true,
          tension: 0.4,
          yAxisID: 'y1',
        },
      ],
    };
  }, [stockData]);

  const vendorPurchaseData = useMemo(() => {
    if (!stockData?.vendor_analytics?.top_vendors) return null;

    const vendors = stockData.vendor_analytics.top_vendors.slice(0, 10);

    return {
      labels: vendors.map((v) => v.vendor_name),
      datasets: [
        {
          label: 'Total Purchases (₹)',
          data: vendors.map((v) => v.total_purchases || 0),
          backgroundColor: [
            '#3b82f6',
            '#8b5cf6',
            '#ec4899',
            '#f59e0b',
            '#10b981',
            '#ef4444',
            '#06b6d4',
            '#f97316',
            '#6366f1',
            '#14b8a6',
          ],
        },
      ],
    };
  }, [stockData]);

  const stockInByVendorData = useMemo(() => {
    if (!stockData?.stock_in_analytics?.by_vendor) return null;

    const vendors = stockData.stock_in_analytics.by_vendor.slice(0, 8);

    return {
      labels: vendors.map((v) => v.vendor_name),
      datasets: [
        {
          label: 'Quantity',
          data: vendors.map((v) => v.quantity || 0),
          backgroundColor: '#10b981',
        },
        {
          label: 'Value (₹)',
          data: vendors.map((v) => v.value || 0),
          backgroundColor: '#059669',
        },
      ],
    };
  }, [stockData]);

  const stockOutByReasonData = useMemo(() => {
    if (!stockData?.stock_out_analytics?.by_reason) return null;

    return {
      labels: stockData.stock_out_analytics.by_reason.map((r) => r.reason),
      datasets: [
        {
          data: stockData.stock_out_analytics.by_reason.map((r) => r.quantity || 0),
          backgroundColor: [
            '#ef4444',
            '#f59e0b',
            '#3b82f6',
            '#8b5cf6',
            '#10b981',
            '#ec4899',
            '#06b6d4',
            '#f97316',
          ],
        },
      ],
    };
  }, [stockData]);

  const topUsedStockData = useMemo(() => {
    if (!stockData?.top_used_stock) return null;

    const items = stockData.top_used_stock.slice(0, 10);

    return {
      labels: items.map((item) => item.item_name),
      datasets: [
        {
          label: 'Quantity Used',
          data: items.map((item) => item.total_quantity_used || 0),
          backgroundColor: '#3b82f6',
        },
      ],
    };
  }, [stockData]);

  const siteBreakdownData = useMemo(() => {
    if (!stockData?.site_breakdown) return null;

    return {
      labels: stockData.site_breakdown.map((s) => s.site_name),
      datasets: [
        {
          label: 'Total Quantity',
          data: stockData.site_breakdown.map((s) => s.total_quantity || 0),
          backgroundColor: '#22c55e',
        },
        {
          label: 'Total Value (₹)',
          data: stockData.site_breakdown.map((s) => s.total_value || 0),
          backgroundColor: '#10b981',
        },
      ],
    };
  }, [stockData]);

  const currentStockBySiteData = useMemo(() => {
    if (!stockData?.current_stock?.items_by_site) return null;

    return {
      labels: stockData.current_stock.items_by_site.map((s) => s.site_name),
      datasets: [
        {
          data: stockData.current_stock.items_by_site.map((s) => s.total_value || 0),
          backgroundColor: [
            '#3b82f6',
            '#8b5cf6',
            '#ec4899',
            '#f59e0b',
            '#10b981',
            '#ef4444',
            '#06b6d4',
            '#f97316',
          ],
        },
      ],
    };
  }, [stockData]);

  // Chart options
  const lineChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString('en-IN')}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: { size: 11 },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: { size: 11 },
          callback: function (value) {
            return value.toLocaleString('en-IN');
          },
        },
      },
      y1: {
        type: 'linear',
        display: true,
        position: 'right',
        beginAtZero: true,
        grid: {
          drawOnChartArea: false,
        },
        ticks: {
          font: { size: 11 },
          callback: function (value) {
            return '₹' + value.toLocaleString('en-IN');
          },
        },
      },
    },
  };

  // Chart options specifically for Daywise Stock Analytics (with units)
  const daywiseStockChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      intersect: false,
      mode: 'index',
    },
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          padding: 15,
          font: {
            size: 12,
            weight: '500',
          },
        },
      },
      tooltip: {
        enabled: true,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        callbacks: {
          label: function (context) {
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString('en-IN')} units`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          maxRotation: 45,
          minRotation: 0,
          font: { size: 11 },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          display: true,
          color: 'rgba(0, 0, 0, 0.05)',
        },
        title: {
          display: true,
          text: 'Quantity (units)',
          font: {
            size: 12,
            weight: '500',
          },
        },
        ticks: {
          font: { size: 11 },
          callback: function (value) {
            return value.toLocaleString('en-IN') + ' units';
          },
        },
      },
    },
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            if (context.dataset.label?.includes('Value')) {
              return `${context.dataset.label}: ₹${context.parsed.y.toLocaleString('en-IN')}`;
            }
            return `${context.dataset.label}: ${context.parsed.y.toLocaleString('en-IN')}`;
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          callback: function (value) {
            return value.toLocaleString('en-IN');
          },
        },
      },
    },
  };

  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right',
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            if (context.dataset.label?.includes('Value')) {
              return `${context.label}: ₹${context.parsed.toLocaleString('en-IN')} (${percentage}%)`;
            }
            return `${context.label}: ${context.parsed.toLocaleString('en-IN')} (${percentage}%)`;
          },
        },
      },
    },
  };

  const pieChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'right',
      },
      tooltip: {
        enabled: true,
        callbacks: {
          label: function (context) {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);
            return `${context.label}: ${context.parsed.toLocaleString('en-IN')} (${percentage}%)`;
          },
        },
      },
    },
  };

  // Summary statistics
  const summary = stockData?.summary || {};
  const currentStock = stockData?.current_stock || {};
  const vendorAnalytics = stockData?.vendor_analytics || {};

  return (
    <div className="w-full h-full">
      {/* Header with Filters */}
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className={`${typography.h2} text-black mb-1`}>Stock Dashboard</h1>
          <p className={`${typography.p} text-gray-600 text-sm`}>
            Comprehensive stock analytics and inventory insights
          </p>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={period}
            onChange={(e) => {
              setPeriod(e.target.value);
              if (e.target.value !== 'custom') {
                setStartDate(null);
                setEndDate(null);
              }
            }}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
            <option value="custom">Custom Range</option>
          </select>

          {period !== 'custom' ? (
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
            />
          ) : (
            <>
              <input
                type="date"
                value={startDate ? formatDateForAPI(startDate) : ''}
                onChange={(e) => setStartDate(e.target.value ? new Date(e.target.value) : null)}
                placeholder="Start Date"
                className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
              />
              <input
                type="date"
                value={endDate ? formatDateForAPI(endDate) : ''}
                onChange={(e) => setEndDate(e.target.value ? new Date(e.target.value) : null)}
                placeholder="End Date"
                className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
              />
            </>
          )}

          <select
            value={selectedSite}
            onChange={(e) => setSelectedSite(e.target.value)}
            disabled={isSitesLoading}
            className="h-10 px-4 bg-white rounded-lg border border-gray-300 focus:border-green-500 focus:outline-none focus:ring-2 focus:ring-green-500/40 text-sm"
          >
            {isSitesLoading ? (
              <option>Loading sites...</option>
            ) : (
              <>
                <option value="">Select a site</option>
                {sites.map((site) => {
                  const siteId =
                    site?.site_id ?? site?.siteId ?? site?.id ?? site?._id ?? '';
                  const siteName = site?.site_name || site?.name || `Site ${siteId}`;
                  return (
                    <option key={siteId} value={String(siteId)}>
                      {siteName}
                    </option>
                  );
                })}
              </>
            )}
          </select>
        </div>
      </div>

      {/* KPI Cards - Summary Metrics */}
      {!isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
          <Card.Gradient
            className="p-5"
            style={{
              background: 'linear-gradient(to bottom right, #3b82f6, #2563eb)',
              boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
              color: '#ffffff',
            }}
          >
            <p className="text-sm opacity-90 mb-1" style={{ color: '#ffffff' }}>Total Stock Items</p>
            <p className="text-2xl font-bold" style={{ color: '#ffffff' }}>
              {summary.total_stock_items?.toLocaleString('en-IN') || '0'}
            </p>
            <p className="text-xs opacity-75 mt-1" style={{ color: '#ffffff' }}>Items in inventory</p>
          </Card.Gradient>

          <Card.Gradient
            className="p-5"
            style={{
              background: 'linear-gradient(to bottom right, #22c55e, #16a34a)',
              boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
              color: '#ffffff',
            }}
          >
            <p className="text-sm opacity-90 mb-1" style={{ color: '#ffffff' }}>Total Stock Value</p>
            <p className="text-2xl font-bold" style={{ color: '#ffffff' }}>
              ₹{summary.total_stock_value?.toLocaleString('en-IN') || '0'}
            </p>
            <p className="text-xs opacity-75 mt-1" style={{ color: '#ffffff' }}>
              {summary.total_current_quantity?.toLocaleString('en-IN') || '0'} units
            </p>
          </Card.Gradient>

          <Card.Gradient
            className="p-5"
            style={{
              background: 'linear-gradient(to bottom right, #f97316, #ea580c)',
              boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
              color: '#ffffff',
            }}
          >
            <p className="text-sm opacity-90 mb-1" style={{ color: '#ffffff' }}>Low Stock Items</p>
            <p className="text-2xl font-bold" style={{ color: '#ffffff' }}>
              {summary.low_stock_items_count?.toLocaleString('en-IN') || '0'}
            </p>
            <p className="text-xs opacity-75 mt-1" style={{ color: '#ffffff' }}>Requires attention</p>
          </Card.Gradient>

          <Card.Gradient
            className="p-5"
            style={{
              background: 'linear-gradient(to bottom right, #a855f7, #9333ea)',
              boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
              color: '#ffffff',
            }}
          >
            <p className="text-sm opacity-90 mb-1" style={{ color: '#ffffff' }}>Net Stock Change</p>
            <p className="text-2xl font-bold" style={{ color: '#ffffff' }}>
              {(summary.net_stock_change ?? 0) >= 0 ? '+' : ''}
              {(summary.net_stock_change ?? 0).toLocaleString('en-IN')}
            </p>
            <p className="text-xs opacity-75 mt-1" style={{ color: '#ffffff' }}>
              In: {summary.total_stock_in_quantity?.toLocaleString('en-IN') || '0'} | Out:{' '}
              {summary.total_stock_out_quantity?.toLocaleString('en-IN') || '0'}
            </p>
          </Card.Gradient>

          <Card.Gradient
            className="p-5 flex flex-col justify-between"
            style={{
              background: 'linear-gradient(to bottom right, #06b6d4, #0891b2)',
              boxShadow: '0px 15px 30px 0px rgba(0,0,0,0.10)',
              color: '#ffffff',
            }}
          >
            <div>
              <p className="text-sm opacity-90 mb-1" style={{ color: '#ffffff' }}>Current Stocks</p>
              
              <p className="text-xs opacity-75 mt-1" style={{ color: '#ffffff' }}>
                Total items available
              </p>
            </div>
            <button
              onClick={() => {
                // Navigate to All Stock Items page with stockData
                if (stockData) {
                  navigate('/dashboard-all-stock-items', {
                    state: { stockData },
                  });
                }
              }}
              disabled={!stockData}
              className="mt-4 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg text-sm font-medium transition-colors border border-white/30 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ color: '#ffffff' }}
            >
              View all Stocks
            </button>
          </Card.Gradient>
        </div>
      )}

      {/* Additional Info Cards - 3 Column Layout */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-4">
            {/* Vendor Summary */}
            <Card.Gradient className="p-6">
              <h4 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                Vendor Summary
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Vendors</span>
                  <span className="font-bold text-lg">
                    {vendorAnalytics.total_vendors?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Purchases</span>
                  <span className="font-bold text-lg">
                    ₹{vendorAnalytics.total_purchases?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Avg Purchase Value</span>
                  <span className="font-bold text-lg">
                    ₹{vendorAnalytics.avg_purchase_value?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
              </div>
            </Card.Gradient>

            {/* Current Stock Summary */}
            <Card.Gradient className="p-6">
              <h4 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                Current Stock Summary
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Items</span>
                  <span className="font-bold text-lg">
                    {currentStock.total_items?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Quantity</span>
                  <span className="font-bold text-lg">
                    {currentStock.total_quantity?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Total Value</span>
                  <span className="font-bold text-lg">
                    ₹{currentStock.total_value?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Low Stock Items</span>
                  <span className="font-bold text-lg text-orange-600">
                    {currentStock.low_stock_items?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
              </div>
            </Card.Gradient>

            {/* Stock Movement Summary */}
            <Card.Gradient className="p-6">
              <h4 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                Stock Movement Summary
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Stock In Quantity</span>
                  <span className="font-bold text-lg text-green-600">
                    +{summary.total_stock_in_quantity?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Stock In Value</span>
                  <span className="font-bold text-lg text-green-600">
                    ₹{summary.total_stock_in_value?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Stock Out Quantity</span>
                  <span className="font-bold text-lg text-red-600">
                    -{summary.total_stock_out_quantity?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-gray-600">Stock Out Value</span>
                  <span className="font-bold text-lg text-red-600">
                    ₹{summary.total_stock_out_value?.toLocaleString('en-IN') || '0'}
                  </span>
                </div>
              </div>
            </Card.Gradient>
          </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex justify-center items-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200 border-t-green-600 mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Loading stock analytics...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <Card.Gradient className="p-8 text-center">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className={typography.h4} style={{ color: '#000000', marginBottom: '0.5rem' }}>
              Error Loading Data
            </h3>
            <p className={typography.p} style={{ color: '#666666' }}>
              {error}
            </p>
          </div>
        </Card.Gradient>
      )}

      {/* Charts Grid */}
      {!isLoading && !error && (
        <div className="space-y-6">
          {/* Main Charts Row */}
          {/* Daywise Stock Analytics */}
          <Card.Gradient className="p-6">
            <h3 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
              Daywise Stock Analytics
            </h3>
            {daywiseStockData ? (
              <div style={{ height: '350px' }}>
                <Line data={daywiseStockData} options={daywiseStockChartOptions} />
              </div>
            ) : (
              <div style={{ height: '350px' }} className="flex items-center justify-center">
                <p className="text-gray-500">No data available for the selected period</p>
              </div>
            )}
          </Card.Gradient>

          {/* Stock Movement Charts - Combined in Single Card */}
          <Card.Gradient className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Stock In Over Time - Left */}
              <div>
                <h3 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                  Stock In Over Time
                </h3>
                {stockInOverTimeData ? (
                  <div style={{ height: '350px' }}>
                    <Line data={stockInOverTimeData} options={lineChartOptions} />
                  </div>
                ) : (
                  <div style={{ height: '350px' }} className="flex items-center justify-center">
                    <p className="text-gray-500">No data available</p>
                  </div>
                )}
              </div>

              {/* Stock Out Over Time - Right */}
              <div>
                <h3 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                  Stock Out Over Time
                </h3>
                {stockOutOverTimeData ? (
                  <div style={{ height: '350px' }}>
                    <Line data={stockOutOverTimeData} options={lineChartOptions} />
                  </div>
                ) : (
                  <div style={{ height: '350px' }} className="flex items-center justify-center">
                    <p className="text-gray-500">No data available</p>
                  </div>
                )}
              </div>
            </div>
          </Card.Gradient>

          {/* Vendor Analytics Row - 2 Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Vendors */}
            <Card.Gradient className="p-6">
              <h3 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                Top Vendors by Purchase Value
              </h3>
              {vendorPurchaseData ? (
                <div style={{ height: '350px' }}>
                  <Bar data={vendorPurchaseData} options={barChartOptions} />
                </div>
              ) : (
                <div style={{ height: '350px' }} className="flex items-center justify-center">
                  <p className="text-gray-500">No data available</p>
                </div>
              )}
            </Card.Gradient>

            {/* Stock In by Vendor */}
            <Card.Gradient className="p-6">
              <h3 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                Stock In by Vendor
              </h3>
              {stockInByVendorData ? (
                <div style={{ height: '350px' }}>
                  <Bar data={stockInByVendorData} options={barChartOptions} />
                </div>
              ) : (
                <div style={{ height: '350px' }} className="flex items-center justify-center">
                  <p className="text-gray-500">No data available</p>
                </div>
              )}
            </Card.Gradient>
          </div>

          {/* Stock Usage & Breakdown Row - 2 Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Top Used Stock */}
            <Card.Gradient className="p-6">
              <h3 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                Top Used Stock Items
              </h3>
              {topUsedStockData ? (
                <div style={{ height: '350px' }}>
                  <Bar data={topUsedStockData} options={barChartOptions} />
                </div>
              ) : (
                <div style={{ height: '350px' }} className="flex items-center justify-center">
                  <p className="text-gray-500">No data available</p>
                </div>
              )}
            </Card.Gradient>

            {/* Stock Out by Reason */}
            <Card.Gradient className="p-6">
              <h3 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
                Stock Out by Reason
              </h3>
              {stockOutByReasonData ? (
                <div style={{ height: '350px' }}>
                  <Pie data={stockOutByReasonData} options={pieChartOptions} />
                </div>
              ) : (
                <div style={{ height: '350px' }} className="flex items-center justify-center">
                  <p className="text-gray-500">No data available</p>
                </div>
              )}
            </Card.Gradient>
          </div>

          {/* Site Breakdown - Full Width */}
          <Card.Gradient className="p-6">
            <h3 className={typography.h5} style={{ color: '#000000', marginBottom: '1rem' }}>
              Site Breakdown
            </h3>
            {siteBreakdownData ? (
              <div style={{ height: '400px' }}>
                <Bar data={siteBreakdownData} options={barChartOptions} />
              </div>
            ) : (
              <div style={{ height: '400px' }} className="flex items-center justify-center">
                <p className="text-gray-500">No data available</p>
              </div>
            )}
          </Card.Gradient>
        </div>
      )}

    </div>
  );
};

export default StockDashboard;

